import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { ToastContext } from '../../services/contexts/toast-context';
import { firebaseGoogleSignin } from '../../services/firebase/auth-with-provider';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { app } from '../../services/firebase/firebase-config';
import { axiosInstance } from '../../services/api/config';
import { useQueryClient } from '@tanstack/react-query';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: var(--bg-main);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const LoginCard = styled(Card)`
  max-width: 400px;
  width: 100%;
  
  .p-card-body {
    padding: 2rem;
  }
  
  .login-header {
    text-align: center;
    margin-bottom: 2rem;
    
    h1 {
      color: var(--text-main);
      margin-bottom: 0.5rem;
    }
    
    p {
      color: var(--text-secondary);
      margin: 0;
    }
  }
  
  .form-group {
    margin-bottom: 1.5rem;
    
    label {
      display: block;
      margin-bottom: 0.5rem;
      color: var(--text-main);
      font-weight: 600;
    }
  }
  
  .login-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 2rem;
  }
  
  .divider {
    text-align: center;
    margin: 1.5rem 0;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      background: var(--border-color);
    }
    
    span {
      background: var(--bg-card);
      padding: 0 1rem;
      color: var(--text-secondary);
      font-size: 0.9rem;
    }
  }
`;

export default function RecruiterLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const toastRef = useContext(ToastContext);
  const queryClient = useQueryClient();

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const { error } = await firebaseGoogleSignin();
      if (error) {
        toastRef.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error,
          life: 3000
        });
      } else {
        // Create backend user record and set recruiter role
        try {
          await axiosInstance.post('users/auth/');
          await axiosInstance.post('users/auth/set_role/', { role: 'recruiter' });
          queryClient.invalidateQueries({ queryKey: ['users'] });
        } catch (err) {
          // User record creation failed — still navigate, onboarding will retry
          console.warn('Backend auth setup:', err);
        }
        navigate('/recruiter/on-boarding');
      }
    } catch (error) {
      toastRef.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to sign in with Google',
        life: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toastRef.current.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please fill in all fields',
        life: 3000
      });
      return;
    }

    setIsLoading(true);
    try {
      const auth = getAuth(app);
      await signInWithEmailAndPassword(auth, email, password);
      // Create backend user record and set recruiter role
      try {
        await axiosInstance.post('users/auth/');
        await axiosInstance.post('users/auth/set_role/', { role: 'recruiter' });
        queryClient.invalidateQueries({ queryKey: ['users'] });
      } catch (err) {
        console.warn('Backend auth setup:', err);
      }
      navigate('/recruiter/on-boarding');
    } catch (error) {
      // If user doesn't exist, try creating an account
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        try {
          const auth = getAuth(app);
          await createUserWithEmailAndPassword(auth, email, password);
          // Create backend user record and set recruiter role
          try {
            await axiosInstance.post('users/auth/');
            await axiosInstance.post('users/auth/set_role/', { role: 'recruiter' });
            queryClient.invalidateQueries({ queryKey: ['users'] });
          } catch (err) {
            console.warn('Backend auth setup:', err);
          }
          navigate('/recruiter/on-boarding');
        } catch (signupError) {
          const msg = signupError.code === 'auth/weak-password'
            ? 'Password must be at least 6 characters'
            : signupError.code === 'auth/email-already-in-use'
            ? 'An account with this email already exists. Check your password.'
            : signupError.message;
          toastRef.current.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
        }
      } else if (error.code === 'auth/wrong-password') {
        toastRef.current.show({ severity: 'error', summary: 'Error', detail: 'Incorrect password', life: 3000 });
      } else {
        toastRef.current.show({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <div className="login-header">
          <h1>Recruiter Login</h1>
          <p>Access your recruiter dashboard to find top talent</p>
        </div>

        <form onSubmit={handleEmailLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <InputText
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <Password
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full"
              disabled={isLoading}
              toggleMask
            />
          </div>

          <div className="login-actions">
            <Button
              type="submit"
              label="Sign In with Email"
              icon="pi pi-envelope"
              className="p-button-primary w-full"
              loading={isLoading}
            />
          </div>
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <div className="login-actions">
          <Button
            label="Sign In with Google"
            icon="pi pi-google"
            className="p-button-outlined p-button-secondary w-full"
            onClick={handleGoogleLogin}
            loading={isLoading}
          />
        </div>
      </LoginCard>
    </LoginContainer>
  );
}


